// [목적] RAG 대화·1화면 생성형 UI 흐름 API 클라이언트 — POST /rag/conversations, POST /rag/conversations/:id/turn,
//        POST /rag/conversations/:id/slots
// [이유] Spec(2026-07-11_openui-oneshot-pdf-folio) §7 D-1~D-6 + Tech Review AD-7(T8):
//        "신규 api/rag.client.ts(fetchWithEnvelope 패턴 + zod). plan.client/SSE 훅 미재사용."
//        CLAUDE §6-5: LLM/외부 응답은 zod 파싱 후 사용 — BE 계약을 이 파일에 미러(SoT는 아래 각 스키마 주석 명시).
//        기존 http.ts 의 fetchWithEnvelope(envelope unwrap·x-anon-session-id·CSRF 자동)를 그대로 재사용.
//        [fe-rag-generate-rewire, 2026-07-19] turn() 추가 — ChatPage/ChatMobile 이 죽은 /plan API 대신
//        되묻기 왕복(1턴씩)을 이 함수로 처리한다(Spec §7 D-3/D-4 확정: SSE·folioId 채널 폐기,
//        완성 후 산출은 기존 submitSlots→폴링→PDF 잡 재사용).
// [사이드 이팩트] createConversation() → BE convId ULID 생성 + Redis state 초기화(+첫 발화 처리).
//                turn() → BE Redis GET/SET + LLM 1회(슬롯 추출) — convId 는 이미 존재해야 함(404 시 세션 재시작 필요).
//                submitSlots() → BE 가 슬롯 병합·재검증·목적지 해석 후 phase='generating' 전환 + folio-pdf 큐 enqueue.
// [수정 시 고려사항] BE 스키마(SoT) 변경 시 이 파일의 미러 스키마도 동시 갱신 필요 — 자동 동기 없음(별도 패키지 미도입, ponytail).
//                   GET /:id/generation(폴링, T7) 은 이번 Wave(T8) 범위 밖 — T9 인계(아래 pollGeneration 자리 주석 참조).

import { z } from 'zod';
import { conceptIdSchema } from '@gangwoncanvas/types';

import { fetchWithEnvelope } from './http.js';

// ── kind 화이트리스트 (BE SoT: apps/api/src/modules/rag/dto/ui-component.schema.ts UiComponentKindEnum) ──

/**
 * [목적] 생성형 UI 컴포넌트 kind 화이트리스트 미러 — 7종 고정
 * [이유] BE ui-component.schema.ts UiComponentKindEnum 과 1:1 미러. LLM 은 kind 를 생성하지 않음(R2) —
 *        FE 는 이 enum 밖의 값을 만나면 discriminatedUnion 파싱이 실패해 텍스트 폴백으로 처리된다(T8 AC).
 * [사이드 이팩트] components/rag/UiComponentRenderer.tsx 의 kind 스위치와 1:1 대응.
 * [수정 시 고려사항] BE kind 추가/변경 시 이 enum + UiComponentSpecSchema union + 렌더러 동시 갱신 필수.
 */
export const UiComponentKindEnum = z.enum([
  'slider',
  'segmented',
  'stepper',
  'chips-multi',
  'chips',
  'daterange',
  'chips-free',
  // [2026-09-21] 출발 날짜 단일 입력 — daterange 는 종료일까지 받아 duration 과 중복되므로 별도 kind
  'date',
]);
export type UiComponentKind = z.infer<typeof UiComponentKindEnum>;

export const SliderParamsSchema = z.object({
  min: z.number(),
  max: z.number(),
  step: z.number(),
  unit: z.string(),
  basis: z.string(),
});

export const SegmentedParamsSchema = z.object({
  options: z.array(z.string()).min(1),
});

export const StepperParamsSchema = z.object({
  min: z.number().int(),
  max: z.number().int(),
  default: z.number().int(),
});

export const ChipsMultiParamsSchema = z.object({
  options: z.array(z.string()).min(1),
  multi: z.literal(true),
});

export const ChipsParamsSchema = z.object({
  options: z.array(z.string()).min(1),
  multi: z.literal(false),
});

export const DaterangeParamsSchema = z.object({
  minDate: z.string(),
  maxDate: z.string(),
});

// [2026-09-21] 단일 날짜 params — BE DateParamsSchema 미러
export const DateParamsSchema = z.object({
  minDate: z.string(),
  maxDate: z.string(),
});

export const ChipsFreeParamsSchema = z.object({
  free: z.literal(true),
});

/**
 * [목적] 생성형 UI 컴포넌트 spec 전체 계약 미러 — slot/kind/label/params(kind별 판별 유니온)
 * [이유] BE SoT: dto/ui-component.schema.ts UiComponentSpecSchema. FE 레지스트리(UiComponentRenderer)가
 *        이 스키마로 개별 컴포넌트를 안전 파싱(safeParse) 후 렌더 — 실패 시 텍스트 폴백(R2/AC).
 * [사이드 이팩트] CreateConversationResponseSchema 의 uiComponents 배열 원소 타입.
 * [수정 시 고려사항] kind 리터럴 추가 시 이 union 에도 branch 추가 필수(BE 동기).
 */
export const UiComponentSpecSchema = z.discriminatedUnion('kind', [
  z.object({
    slot: z.string(),
    kind: z.literal('slider'),
    label: z.string(),
    params: SliderParamsSchema,
  }),
  z.object({
    slot: z.string(),
    kind: z.literal('segmented'),
    label: z.string(),
    params: SegmentedParamsSchema,
  }),
  z.object({
    slot: z.string(),
    kind: z.literal('stepper'),
    label: z.string(),
    params: StepperParamsSchema,
  }),
  z.object({
    slot: z.string(),
    kind: z.literal('chips-multi'),
    label: z.string(),
    params: ChipsMultiParamsSchema,
  }),
  z.object({
    slot: z.string(),
    kind: z.literal('chips'),
    label: z.string(),
    params: ChipsParamsSchema,
  }),
  z.object({
    slot: z.string(),
    kind: z.literal('daterange'),
    label: z.string(),
    params: DaterangeParamsSchema,
  }),
  z.object({
    slot: z.string(),
    kind: z.literal('chips-free'),
    label: z.string(),
    params: ChipsFreeParamsSchema,
  }),
  z.object({
    slot: z.string(),
    kind: z.literal('date'),
    label: z.string(),
    params: DateParamsSchema,
  }),
]);
export type UiComponentSpec = z.infer<typeof UiComponentSpecSchema>;

// ── POST /rag/conversations 응답 (BE SoT: dto/rag-response.dto.ts CreateConversationResponseSchema) ──

/**
 * [목적] 대화 type 필드 유니온 미러 — OQ-6 확정 계약(ask/clarify/final/ready)
 * [이유] BE mapPhaseToType() 산출값과 1:1 대응. 1화면 흐름은 clarify 를 호출하지 않아야 하지만(AC),
 *        방어적으로 계약 전체를 파싱해 drift 를 조기 감지한다.
 * [사이드 이팩트] 없음(타입 정의).
 * [수정 시 고려사항] BE 유니온 확장 시 동기 갱신.
 */
export const TurnResponseTypeSchema = z.enum(['ask', 'clarify', 'final', 'ready']);

/**
 * [목적] clarify 선택지 메타 미러 — BE ClarifyMetaSchema 1:1(slot/kind/candidates)
 * [이유] BE ClarifyMetaSchema 필드 구조 미러(candidates.value 는 BE 도 z.unknown()).
 *        1화면 생성형 UI 흐름(createConversation 최초 진입)은 이 필드를 소비하지 않지만(방어적 파싱만),
 *        [fe-rag-generate-rewire, 2026-07-19] 챗 되묻기 왕복(turn() 반복)에서는 type='clarify' 응답의
 *        candidates 를 실제로 칩 UI 로 렌더한다(ChatPage/ChatMobile) — 그래서 export 하여 재사용.
 * [사이드 이팩트] 없음.
 * [수정 시 고려사항] candidates 미제공(옵셔널) 시 칩 없이 reply 텍스트만 표시 — 호출부가 방어적으로 처리해야 함.
 */
export const ClarifyMetaSchema = z
  .object({
    slot: z.string(),
    kind: z.string().optional(),
    candidates: z.array(z.object({ label: z.string(), value: z.unknown() })).optional(),
  })
  .optional();
export type ClarifyMeta = z.infer<typeof ClarifyMetaSchema>;

/**
 * [목적] POST /rag/conversations 응답 스키마 미러
 * [이유] BE CreateConversationResponseSchema 필드 중 이 화면이 실제로 소비하는 필드만 엄격 파싱하고,
 *        `state`(ConversationState 전체)는 이번 Wave(T8) 에서 내부를 들여다보지 않으므로 z.unknown() 으로
 *        불투명 처리한다(ponytail — 미사용 중첩 스키마 전체를 복제하지 않음). T9 폴링 훅이 별도
 *        GET /:id/generation(T7) 응답을 소비할 때 필요한 스키마를 그때 추가한다.
 *        `uiComponents` 는 배열 원소를 여기서 UiComponentSpecSchema 로 엄격 검증하지 않는다(z.unknown()[]) —
 *        R2/AC: "미지원 kind·params 검증 실패는 텍스트 폴백"은 컴포넌트 단위 요구사항이므로, 한 항목이
 *        깨졌다고 응답 전체 parse(.parse, throw)가 실패해 1화면 폼 전체가 무너지면 안 된다. 개별 검증은
 *        components/rag/UiComponentRenderer.tsx 가 항목별 safeParse 로 수행(폴백은 항목 단위로 격리).
 * [사이드 이팩트] state 필드는 파싱만 통과(any) — 이 값을 읽어 분기하는 코드는 작성하지 않는다.
 * [수정 시 고려사항] BE 가 uiComponents 를 채우지 않는 경우(예: turn 경로) undefined 로 온다 — 페이지는 빈 배열로 취급.
 */
export const CreateConversationResponseSchema = z.object({
  convId: z.string().min(1),
  type: TurnResponseTypeSchema,
  reply: z.string().nullable(),
  clarifyMeta: ClarifyMetaSchema,
  state: z.unknown(),
  uiComponents: z.array(z.unknown()).optional(),
});
export type CreateConversationResponse = z.infer<typeof CreateConversationResponseSchema>;

// ── POST /rag/conversations/:id/slots 요청/응답 (BE SoT: dto/slot-submit.schema.ts, dto/slot.schema.ts) ──

/**
 * [목적] duration/budget 슬롯 부분 구조체 미러 — slot.schema.ts DurationSlotSchema/BudgetSlotSchema 필드 일치
 * [이유] 1화면 폼이 실제로 채우는 하드필수 5종(duration/companions/headcount/budget/wants) 중
 *        중첩 객체 2종만 별도 정의(ponytail — AccumulatedSlotSchema 전체를 미러하지 않고 이번 Wave 가
 *        실제 제출하는 필드만 타입화. destination/origin/departTime/transport/preferences/dislikes/
 *        mustVisit 은 BE 가 옵셔널로 허용하므로 FE 가 생략해도 기존 state 값이 유지된다).
 * [사이드 이팩트] 없음(zod 스키마 정의).
 * [수정 시 고려사항] BE DurationSlotSchema/BudgetSlotSchema 필드 변경 시 이 스키마도 동기 갱신 필요.
 */
const DurationSlotInputSchema = z.object({
  nights: z.number().int().min(0).nullable(),
  days: z.number().int().min(1).nullable(),
  assumed: z.boolean(),
});

const BudgetSlotInputSchema = z.object({
  amount: z.number().nullable(),
  unit: z.enum(['원', '만원', '천원']).nullable(),
  basis: z.enum(['1인당', '총액']).nullable(),
  assumed: z.boolean(),
});

/**
 * [목적] POST /:id/slots 요청 바디 스키마 — 1화면 폼 하드필수 5종 + concept(컨셉 칩) partial 허용
 * [이유] BE SlotSubmitSchema(AccumulatedSlotSchema.omit({ambiguous,needsConfirmation}).partial())의
 *        부분집합. FE 는 gap-eval.HARD_REQUIRED_SLOTS 5종(duration/companions/headcount/budget/wants)만
 *        렌더·수집하므로(T1 buildUiComponents 범위와 동일) 그 외 필드는 보내지 않는다 — BE 가 기존 값 유지.
 *        [FlowLine 83963a6b, 2026-09-18] concept 필드 추가 — 채팅 추천 칩(useConcepts) 클릭으로
 *        선택된 ConceptId 를 useChatFlow.ts 가 confirmGenerate() 최종 제출 시 함께 실어 보낸다
 *        (slot.schema.ts ConceptEnum 과 값 집합 동일, packages/types conceptIdSchema 재사용).
 * [사이드 이팩트] 없음(요청 바디 검증).
 * [수정 시 고려사항] 2/3단 슬롯(daterange→startDate, chips-free→dislikes/mustVisit) 컴포넌트가 후속 Wave 에서
 *                   BE 에 추가되면 이 스키마에 해당 필드도 추가해야 제출 가능(현재는 렌더러만 지원, 매핑은 없음).
 */
// [2026-09-20/21] transport(하드필수 승격) · rangePreference(1화면 노출) 추가.
// BE slot.schema.ts 의 TransportEnum / RangePreferenceEnum 과 값 집합이 반드시 같아야 한다.
const TransportSlotInputSchema = z.object({
  value: z.enum(['자가용', '대중교통', '렌터카', '기타', '기차', '시외버스']).nullable(),
  assumed: z.boolean(),
});

// [2026-09-21] 칩 enum → 정수 km. 0 = 거리 제한 없음, null = 미지정(BE rangePreference 계약 동일).
const RangeSlotInputSchema = z.object({
  value: z.number().int().min(0).max(200).nullable(),
  assumed: z.boolean(),
});

// [2026-09-21] 출발 날짜 — BE slot.schema.ts startDate 와 동일한 'YYYY-MM-DD' 계약
const StartDateSlotInputSchema = z.object({
  value: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
  assumed: z.boolean(),
});

export const SlotSubmitBodySchema = z
  .object({
    duration: DurationSlotInputSchema,
    companions: z.string().nullable(),
    headcount: z.number().int().min(1).nullable(),
    budget: BudgetSlotInputSchema,
    wants: z.array(z.string()),
    concept: conceptIdSchema.nullable(),
    transport: TransportSlotInputSchema,
    rangePreference: RangeSlotInputSchema,
    startDate: StartDateSlotInputSchema,
  })
  .partial();
export type SlotSubmitBody = z.infer<typeof SlotSubmitBodySchema>;

/** buildSlotFragment 가 쓰는 좁힌 값 타입 — 위 스키마와 단일 출처 유지 */
export type SlotSubmitTransport = NonNullable<z.infer<typeof TransportSlotInputSchema>['value']>;
export type SlotSubmitRange = NonNullable<z.infer<typeof RangeSlotInputSchema>['value']>;

/**
 * [목적] POST /:id/slots 성공 응답 스키마 미러 — 202 {status:'generating'}
 * [이유] BE SoT: dto/slot-submit.schema.ts SlotSubmitResponseSchema.
 * [사이드 이팩트] 없음.
 * [수정 시 고려사항] status 값 확장 시 BE 동기 갱신 확인.
 */
export const SlotSubmitResponseSchema = z.object({
  status: z.literal('generating'),
});
export type SlotSubmitResponse = z.infer<typeof SlotSubmitResponseSchema>;

/**
 * [목적] 400 rag.SLOTS_INCOMPLETE 에러 details 페이로드 스키마 — 부족 슬롯 목록
 * [이유] BE rag.controller.ts submitSlots(): `throw new BizException('rag.SLOTS_INCOMPLETE', undefined,
 *        { details: { missingSlots: unfilledSlots } })`. FE 가 ApiError.details 를 안전 파싱해 표시.
 * [사이드 이팩트] 없음.
 * [수정 시 고려사항] BE details 구조 변경 시 동기 갱신(현재는 missingSlots 문자열 배열만 있음).
 */
export const SlotsIncompleteDetailsSchema = z.object({
  missingSlots: z.array(z.string()),
});

// ── API 함수 ─────────────────────────────────────────────────────────────────

/**
 * [목적] POST /rag/conversations — 새 대화 세션 생성(+ 첫 발화 자연어 한 줄)
 * [이유] Spec §2 FE In scope: "자연어 한 줄 입력 진입점(1개 텍스트필드) 유지". utterance 없으면 세션만 생성.
 * [사이드 이팩트] BE Redis SET(초기 state) + (첫 발화 있으면) LLM 1회 호출.
 * [수정 시 고려사항] fetchWithEnvelope 가 x-anon-session-id/CSRF 를 자동 첨부 — 별도 인자 불필요(익명 허용).
 */
export async function createConversation(utterance?: string): Promise<CreateConversationResponse> {
  const data = await fetchWithEnvelope<unknown>('/rag/conversations', {
    method: 'POST',
    body: JSON.stringify(utterance ? { utterance } : {}),
  });
  return CreateConversationResponseSchema.parse(data);
}

// ── POST /rag/conversations/:id/turn 응답 (BE SoT: dto/rag-response.dto.ts TurnResponseSchema) ──

/**
 * [목적] POST /:id/turn 응답 스키마 미러 — createConversation 과 동일 계약이나 convId 필드 없음(경로에 이미 포함)
 * [이유] BE TurnResponseSchema(rag-response.dto.ts) 1:1 미러: { type, reply, clarifyMeta?, state, notice?, uiComponents? }.
 *        fe-rag-generate-rewire Spec §7 D-3/D-4: 챗은 이 응답의 type/reply/clarifyMeta 만 소비 —
 *        state 는 CreateConversationResponseSchema 와 동일하게 z.unknown() 으로 불투명 처리(ponytail).
 *        notice(확정 슬롯 잠금 안내 한 줄)는 챗 말풍선으로 추가 표시한다(useChatFlow.responseToMessages).
 * [사이드 이팩트] 없음(타입 정의).
 * [수정 시 고려사항] BE 필드 추가/변경 시 이 스키마와 CreateConversationResponseSchema 동시 갱신 검토.
 */
export const TurnResponseSchema = z.object({
  type: TurnResponseTypeSchema,
  reply: z.string().nullable(),
  clarifyMeta: ClarifyMetaSchema,
  state: z.unknown(),
  notice: z.string().optional(),
  uiComponents: z.array(z.unknown()).optional(),
});
export type TurnResponse = z.infer<typeof TurnResponseSchema>;

/**
 * [목적] POST /rag/conversations/:id/turn — 대화 턴 1회 처리(기존 세션에 발화 1건 전송)
 * [이유] fe-rag-generate-rewire Spec §7 D-3/D-4 확정 구현 스코프: "이후 후속 입력은 turn(convId, utterance)
 *        반복". createConversation() 은 세션 생성(+ 첫 발화)만 담당하고, 이후 모든 턴은 이 함수로 처리한다.
 * [사이드 이팩트] BE Redis GET/SET(state 갱신) + LLM 1회(슬롯 추출/모호성 판별).
 *                clarify 응답 시 clarifyMeta.candidates 로 칩을 렌더 — 칩 클릭 값을 문자열화해 다시 이 함수로 전송.
 * [수정 시 고려사항] convId 가 만료/미존재(404 rag.SESSION_NOT_FOUND)면 호출부가 새 세션 시작을 유도해야 함
 *                   (본 Wave 는 별도 세션 복구 UX 미구현 — D-1: 새로고침=새 대화로 충분, 런타임 404는 폴백 안내만).
 */
export async function turn(convId: string, utterance: string): Promise<TurnResponse> {
  const data = await fetchWithEnvelope<unknown>(`/rag/conversations/${convId}/turn`, {
    method: 'POST',
    body: JSON.stringify({ utterance }),
  });
  return TurnResponseSchema.parse(data);
}

/**
 * [목적] POST /rag/conversations/:id/slots — 구조화 슬롯 직접 제출(슬롯그래프 되묻기 skip)
 * [이유] Spec AD-4: 1화면 보완 폼에서 채운 값을 즉시 제출해 되묻기 왕복 없이 생성 잡을 시작한다.
 * [사이드 이팩트] 성공(202) 시 BE 가 phase='generating' 전환 + folio-pdf 큐 enqueue.
 *                실패(400 rag.SLOTS_INCOMPLETE) 시 ApiError.details.missingSlots 로 부족 슬롯 안내 가능.
 * [수정 시 고려사항] 호출부가 ApiError.code==='rag.SLOTS_INCOMPLETE' 분기를 반드시 처리해야 한다(AC).
 */
export async function submitSlots(
  convId: string,
  body: SlotSubmitBody,
): Promise<SlotSubmitResponse> {
  const validated = SlotSubmitBodySchema.parse(body);
  const data = await fetchWithEnvelope<unknown>(`/rag/conversations/${convId}/slots`, {
    method: 'POST',
    body: JSON.stringify(validated),
  });
  return SlotSubmitResponseSchema.parse(data);
}

// ── GET /rag/conversations/:id/generation 응답 (BE SoT: dto/generation-status.schema.ts) ──

/**
 * [목적] 생성 잡 진행 상태 유니온 미러 — idle(제출 전)|generating(대기)|running(진행)|done|failed
 * [이유] BE GenerationStatusValueSchema 1:1 미러(GenerationMetaSchema.status + 'idle' 합성).
 * [사이드 이팩트] hooks/useFolioGeneration.ts 의 폴링 중단 조건(done/failed)이 이 값에 의존.
 * [수정 시 고려사항] BE 유니온 확장 시 동기 갱신 — 폴링 중단 조건도 함께 재검토.
 */
export const GenerationStatusValueSchema = z.enum([
  'idle',
  'generating',
  'running',
  'done',
  'failed',
]);
export type GenerationStatusValue = z.infer<typeof GenerationStatusValueSchema>;

/**
 * [목적] D-6 단계 progress 유니온 미러 — extract(정보 정리)→search(장소 탐색)→generate(이야기 생성)→render(PDF 렌더)
 * [이유] BE GenerationMetaSchema.stage 1:1 미러. idle/생성 전엔 null.
 * [사이드 이팩트] NewFolioPage 의 단계 라벨/스테퍼 인덱스 매핑이 이 순서에 의존.
 * [수정 시 고려사항] BE 단계 추가/순서 변경 시 STAGE_ORDER(NewFolioPage.tsx)도 동기 갱신 필요.
 */
export const GenerationStageEnum = z.enum(['extract', 'search', 'generate', 'render']);
export type GenerationStage = z.infer<typeof GenerationStageEnum>;

/**
 * [목적] GET /:id/generation 응답 스키마 미러 — status/stage/pdfUrl/expiresAt/errorCode/portfolioId 6필드
 * [이유] BE SoT: dto/generation-status.schema.ts GenerationStatusResponseSchema. T9 폴링 훅(useFolioGeneration)
 *        이 이 스키마로 매 폴링 응답을 파싱해 drift 를 조기 감지한다(CLAUDE §6-5).
 *        [fe-rag-generate-rewire 과업1] portfolioId 필드 추가 — BE 가 FolioDraft 를 trip_portfolios 로
 *        영속한 결과 id 를 실어 보낸다(status 와 무관하게 존재하면 그대로 노출, BE 주석 근거).
 *        FE 는 이 값이 있으면 /folio/:portfolioId 딥링크 CTA 를 노출한다(NewFolioPage/ChatPage/ChatMobile).
 * [사이드 이팩트] 없음.
 * [수정 시 고려사항] BE 필드 추가/삭제 시 이 스키마도 동기 갱신 필요.
 *                   portfolioId 는 BE 계약상 항상 존재(nullable, optional 아님)하지만 FE 미러는
 *                   .optional() 도 허용한다 — G1-1 도입 이전에 작성된 e2e/new-folio-pdf-flow.e2e.ts
 *                   고정 fixture(사용자 미추적, 편집 금지 대상)처럼 필드 자체가 없는 구 mock 도 후방호환
 *                   파싱되어야 하기 때문(다른 nullable().optional() 필드들과 동일 관행). 누락 시
 *                   useFolioGeneration.ts 가 `?? null` 로 정규화한다.
 */
export const GenerationStatusResponseSchema = z.object({
  status: GenerationStatusValueSchema,
  stage: GenerationStageEnum.nullable(),
  pdfUrl: z.string().nullable(),
  expiresAt: z.string().nullable(),
  errorCode: z.string().nullable(),
  portfolioId: z.string().nullable().optional(),
});
export type GenerationStatusResponse = z.infer<typeof GenerationStatusResponseSchema>;

/**
 * [목적] GET /rag/conversations/:id/generation — 생성 잡 진행 상태 폴링
 * [이유] Spec D-6/AD-6: 잡이 단계(추출→검색→생성→렌더)로 진행하므로 FE 가 주기적으로 조회해 진행률을 표시한다.
 * [사이드 이팩트] 없음(순수 조회) — BE 는 Redis GET 1회만 수행(부수효과 없음).
 * [수정 시 고려사항] 폴링 간격/중단 조건은 호출부(useFolioGeneration)가 결정 — 이 함수는 단발 조회만 담당.
 */
export async function getGenerationStatus(convId: string): Promise<GenerationStatusResponse> {
  const data = await fetchWithEnvelope<unknown>(`/rag/conversations/${convId}/generation`);
  return GenerationStatusResponseSchema.parse(data);
}

/**
 * [목적] GET /rag/conversations/:id/pdf — PDF 다운로드 URL 헬퍼(바이너리라 fetchWithEnvelope 미경유)
 * [이유] PDF 는 application/pdf 바이너리 응답(envelope JSON 이 아님) — 브라우저 <a href download> 로
 *        직접 네비게이션시키는 편이 blob 변환보다 단순하다(ponytail). http.ts 의 apiBase 처리 방식과 동일하게
 *        상대경로(apiBase='') 또는 절대경로(apiBase 지정)를 그대로 반환한다.
 *        AnonOrAuthenticatedGuard 는 x-anon-session-id 헤더가 없어도 통과(actor=null 허용, 근거:
 *        anon-or-authenticated.guard.ts) — convId(ULID) 자체가 사실상의 조회 키이므로 커스텀 헤더 없이
 *        <a> 네비게이션만으로 다운로드가 성립한다.
 * [사이드 이팩트] 없음(URL 문자열 조립만).
 * [수정 시 고려사항] BE 라우트(:id/pdf) 변경 시 이 경로도 동기 갱신.
 */
export function buildPdfUrl(convId: string): string {
  const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
  return `${apiBase}/rag/conversations/${convId}/pdf`;
}
